import React, { useCallback, useEffect, useRef, useState } from 'react'

import { ActionDropdown } from '@driftt/tide-core'
import { Icon } from '@driftt/tide-icons'

import { Insight } from 'modules/accountActivity/domain'
import { pluralize } from 'utils/formatStrings'
import { insightUrl } from 'utils/paths'
import { segmentTrack, TrackType } from 'utils/tracking'

import Comments from './Comments'

import styles from './styles.module.css'

interface CardActionsProps {
  insight: Insight
  commentsInitiallyOpen?: boolean
}

const getCommentDescription = (numComments: number) => {
  return numComments === 0
    ? 'No comments yet'
    : `${numComments} ${pluralize('comment', numComments)}`
}

const CardActions: React.FC<CardActionsProps> = ({ insight, commentsInitiallyOpen = false }) => {
  const commentsRef = useRef<any>(null)
  const [showComments, setShowComments] = useState<boolean>(commentsInitiallyOpen)
  const [autofocusComposer, setAutofocusComposer] = useState<boolean>(true)
  const [numComments, setNumComments] = useState<number>(
    insight.comments?.filter(c => !c.deleted).length || 0
  )
  const [shareClicked, setShareClicked] = useState<boolean>(false)

  const onCommentAdded = useCallback(() => setNumComments(numComments + 1), [
    numComments,
    setNumComments
  ])

  const onCommentRemoved = useCallback(
    (newNumComments: number) => setNumComments(newNumComments),
    []
  )

  const handleShareClicked = useCallback(() => {
    navigator.clipboard.writeText(insightUrl(insight.id))
    setShareClicked(true)

    segmentTrack(TrackType.COPIED_LINK_TO_CCRM_FEED_CARD, {
      cardType: insight.insightType,
      event: insight.insightType + ' LINK COPIED',
      insight: insight.id,
      accountId: insight?.accountId
    })
  }, [insight.accountId, insight.id, insight.insightType])

  useEffect(() => {
    if (shareClicked) {
      const timeout = setTimeout(() => setShareClicked(false), 3000)
      return () => clearTimeout(timeout)
    }
  }, [shareClicked])

  return (
    <div className={styles.cardActionContainer}>
      <div className={styles.commentDescription}>
        <span
          className={styles.commentDescriptionText}
          onClick={() => {
            setAutofocusComposer(false)
            setShowComments(!showComments)

            segmentTrack(TrackType.COMMENT_COUNT_CLICKED, {
              cardType: insight.insightType,
              insight: insight.id,
              accountId: insight?.accountId
            })
          }}>
          {getCommentDescription(numComments)}
        </span>
      </div>
      <div className={styles.cardActionButtons}>
        <div
          className={styles.cardActionButton}
          onClick={() => {
            setAutofocusComposer(true)
            setShowComments(true)
            if (commentsRef?.current) {
              commentsRef.current.focusMainComposer()
            }

            segmentTrack(TrackType.COMMENT_ACTION_CLICKED, {
              cardType: insight.insightType,
              insight: insight.id,
              accountId: insight?.accountId
            })
          }}>
          <Icon name="message-1" height={14} width={16} />
          Comment
        </div>
        <ActionDropdown
          className={styles.cardActionButton}
          openTriggerClassName={styles.actionButtonActive}
          flowUp
          trigger={
            <div className={styles.shareActionButton}>
              <Icon name="share-1" height={14} width={16} />
              Share
            </div>
          }
          actions={[
            {
              key: 'copy',
              label: `${shareClicked ? 'Copied!' : 'Copy sharing link'}`,
              action: handleShareClicked
            }
          ]}
        />
      </div>
      {showComments && (
        <Comments
          ref={commentsRef}
          insightId={insight.id}
          initialComments={insight.comments || []}
          onCommentAdded={onCommentAdded}
          onCommentRemoved={onCommentRemoved}
          autofocusComposer={autofocusComposer}
        />
      )}
    </div>
  )
}

export default CardActions
